import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { APP_CONFIG } from '@application/config';
import { withUtcDateFields } from '@application/data-access';
import type {
  IRegistrationCandidate,
  IRegistrationCandidateList,
  IRegistrationCandidateListQuery,
} from '../entities/registration-candidate.model';

/**
 * Superfície autenticada do Worker de pré-cadastro, usada ao balcão.
 *
 * O inquilino **não** viaja em nenhum pedido: o Worker deriva-o do bearer. E o
 * `withCredentials: true` é o que faz o interceptor pendurar o bearer — sem ele
 * o pedido sai anónimo e volta 401.
 */
@Injectable({ providedIn: 'root' })
export class RegistrationCandidateService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(APP_CONFIG).apiUrl.registration_service;

  /**
   * The two times of a candidate. Both are real instants in UTC, stamped by the
   * Worker's own clock, and neither name ends in `Utc`, so the request has to
   * say so for the `DateInterceptor` to bring them to the account's time.
   * Undeclared, the digits of UTC were read as local: in Brazil a registration
   * sent at 22:30 showed as sent at 01:30 of the next day.
   *
   * The interceptor matches a key by its own name at any depth, which is how
   * `acceptedAt` reaches `parq.consent.acceptedAt`. A dotted path would match
   * nothing.
   */
  private readonly instantFields = ['createdAt', 'acceptedAt'];

  /**
   * The times of a line of a list: when it was sent, and when it went to the
   * trash or was handled. Instants in UTC as well, for the same reason. A list
   * carries no health answers, so there is no consent time in it.
   */
  private readonly listInstantFields = ['createdAt', 'trashedAt', 'importedAt'];

  /**
   * Devolve a candidata PENDENTE com este CPF, ou 404 quando não há nenhuma.
   * O Worker já filtra por `status = 'pending'`: uma candidata importada também
   * responde 404.
   *
   * O `?? ''` não é enfeite: um `null` vindo do formulário faria o `.replace`
   * lançar dentro do `switchMap`, fora do `tapResponse`, e matava a assinatura
   * do `rxMethod` até recarregar a página.
   */
  public getByFiscalIdentity(
    fiscalIdentity: string,
    linkId?: string
  ): Observable<IRegistrationCandidate> {
    // The counter picks the link and looks the CPF up inside it: a link takes
    // a CPF once, so the answer is one registration or none. Without a link the
    // Worker looks in the gym's default one, the only one an older Worker has.
    let params = new HttpParams().set('fiscalIdentity', (fiscalIdentity ?? '').replaceAll(/\D/g, ''));
    if (linkId) params = params.set('pageId', linkId);

    return this.http.get<IRegistrationCandidate>(`${this.baseUrl}/api/candidates`, {
      params,
      context: withUtcDateFields(this.instantFields),
      withCredentials: true,
    });
  }

  /** One of the three lists of registrations, a page at a time. Never with health answers. */
  public list(query: IRegistrationCandidateListQuery): Observable<IRegistrationCandidateList> {
    let params = new HttpParams()
      .set('status', query.status)
      .set('limit', query.limit)
      .set('offset', query.offset);
    if (query.linkId) params = params.set('pageId', query.linkId);

    return this.http.get<IRegistrationCandidateList>(`${this.baseUrl}/api/candidates`, {
      params,
      context: withUtcDateFields(this.listInstantFields),
      withCredentials: true,
    });
  }

  /**
   * Deletes a registration. One still waiting goes to the trash, from where it
   * can be restored for 15 days; a handled one is deleted for good. Either way
   * its CPF can be sent through that link again at once.
   */
  public remove(candidateId: string): Observable<{ status: 'trashed' | 'deleted' }> {
    return this.http.delete<{ status: 'trashed' | 'deleted' }>(this.url(candidateId), {
      withCredentials: true,
    });
  }

  /** Out of the trash. 409 when the link already has another registration for that CPF. */
  public restore(candidateId: string): Observable<{ status: 'pending' }> {
    return this.http.post<{ status: 'pending' }>(`${this.url(candidateId)}/restore`, null, {
      withCredentials: true,
    });
  }

  /**
   * 404 = não existe ou é de outro inquilino · 409 = já estava importada.
   *
   * The Worker that has the trash answers 409 as well for a registration that
   * is in the trash, which it does not mark. Only the text of the message
   * tells that answer from "already imported".
   */
  public markImported(candidateId: string, personId: string): Observable<{ status: 'imported' }> {
    return this.http.post<{ status: 'imported' }>(
      `${this.url(candidateId)}/import`,
      { personId },
      { withCredentials: true }
    );
  }

  private url(candidateId: string): string {
    return `${this.baseUrl}/api/candidates/${encodeURIComponent(candidateId)}`;
  }
}
